import { useMemo, useState } from "react";
import { Check, ListFilter } from "lucide-react";
import t from "@/lang/lang";
import { addressListLabel } from "@/services/backend/addresses/types/address";
import { BuildingWithoutAreas } from "@/services/backend/buildings/types/building";
import { Filter, FilterMode } from "@/shared/lib/filter/filter";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { cn } from "@/shared/lib/utils";

const withoutAddressValue = "__without_address__";

export function BuildingAddressFilter({
  buildings,
  filter,
  onFilterChange,
}: {
  buildings?: BuildingWithoutAreas[];
  filter?: Filter<string>;
  onFilterChange: (filter: Filter<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const options = useMemo(() => collectAddressOptions(buildings), [buildings]);
  const currentValue = selectedAddressValue(filter);
  const currentLabel = selectedAddressLabel(filter, options);

  const onSelect = (value: string) => () => {
    if (value === withoutAddressValue) {
      if (filter?.active && filter.mode === FilterMode.IsNull) {
        onFilterChange({ active: false, values: [] });
      } else {
        onFilterChange({ active: true, mode: FilterMode.IsNull, values: [] });
      }
      setOpen(false);
      return;
    }

    if (currentValue === value) {
      onFilterChange({ active: false, values: [] });
      setOpen(false);
      return;
    }

    onFilterChange({ active: true, mode: FilterMode.Like, values: [value] });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="max-w-[22rem] justify-between"
        >
          <ListFilter className="mr-2 h-4 w-4" />
          <span className="truncate">{currentLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(42rem,calc(100vw-2rem))] p-1"
        align="start"
      >
        <ScrollArea className="max-h-[260px]">
          <div className="text-foreground p-1">
            <button
              type="button"
              onClick={() => {
                onFilterChange({ active: false, values: [] });
                setOpen(false);
              }}
              className="hover:bg-accent flex w-full cursor-default items-start rounded-sm px-2 py-1.5 text-left text-sm outline-none select-none"
            >
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  !filter?.active ? "opacity-100" : "opacity-0",
                )}
              />
              {t("Alle Adressen")}
            </button>
            <button
              type="button"
              onClick={onSelect(withoutAddressValue)}
              className="hover:bg-accent flex w-full cursor-default items-start rounded-sm px-2 py-1.5 text-left text-sm outline-none select-none"
            >
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  filter?.active && filter.mode === FilterMode.IsNull
                    ? "opacity-100"
                    : "opacity-0",
                )}
              />
              {t("Ohne Adresse")}
            </button>
            {options.map((option) => (
              <button
                type="button"
                key={option}
                onClick={onSelect(option)}
                className="hover:bg-accent flex w-full cursor-default items-start rounded-sm px-2 py-1.5 text-left text-sm outline-none select-none"
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    currentValue === option ? "opacity-100" : "opacity-0",
                  )}
                />
                <span className="line-clamp-2 break-words">{option}</span>
              </button>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function collectAddressOptions(buildings?: BuildingWithoutAreas[]) {
  const values = new Set<string>();
  for (const building of buildings ?? []) {
    const value = addressFilterValue(building);
    if (value !== "") {
      values.add(value);
    }
  }
  return Array.from(values.values()).sort((left, right) =>
    left.localeCompare(right, "de"),
  );
}

function selectedAddressValue(filter?: Filter<string>) {
  if (!filter?.active || filter.mode === FilterMode.IsNull) {
    return "";
  }
  return filter.values?.[0] ?? "";
}

function selectedAddressLabel(
  filter: Filter<string> | undefined,
  options: string[],
) {
  if (!filter?.active) {
    return t("Adresse");
  }
  if (filter.mode === FilterMode.IsNull) {
    return t("Ohne Adresse");
  }
  const selected = filter.values?.[0] ?? "";
  if (selected === "") {
    return t("Adresse");
  }
  if (options.includes(selected)) {
    return selected;
  }
  return selected;
}

function addressFilterValue(building: BuildingWithoutAreas) {
  const value = addressListLabel(building.address).trim();
  if (value === "") {
    return "";
  }
  return value;
}
